/**
 * Storefront orders — merchant side.
 *
 * Orders placed on the public storefront (gestockweb): either a WhatsApp intent
 * or a full manual order with name, phone, quantity, address and notes.
 */

import apiClient from "../api";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unwrap = (error: any, fallback: string) =>
  error?.response?.data || error || { message: fallback };

export type OrderStatus =
  | "NEW"
  | "CONTACTED"
  | "CONFIRMED"
  | "PREPARING"
  | "SHIPPED"
  | "DELIVERED"
  | "CANCELLED"
  | "LOST";

export type OrderSource = "WHATSAPP" | "MANUAL";

export interface OrderLine {
  productName: string;
  productIdentifier: string;
  variantAttributes: Record<string, string | number>;
  quantity: number;
  unitPriceFcfa: number;
  lineTotalFcfa: number;
}

export interface StoreOrder {
  _id: string;
  reference: string;
  source: OrderSource;
  status: OrderStatus;
  customerName: string;
  customerPhone: string;
  customerNotes: string;
  deliveryMode: "PICKUP" | "DELIVERY";
  deliveryAddress: string;
  deliveryCity: string;
  deliveryNotes: string;
  preferredContactChannel: string;
  lines: OrderLine[];
  subtotalFcfa: number;
  deliveryFeeFcfa: number;
  totalFcfa: number;
  internalNotes: string;
  statusHistory: { status: string; note: string; changedAt: string }[];
  createdAt: string;
}

export interface OrdersPage {
  orders: StoreOrder[];
  pagination: { page: number; limit: number; total: number; pages: number };
  summary: {
    total: number;
    new: number;
    contacted: number;
    confirmed: number;
    delivered: number;
    cancelled: number;
    revenueFcfa: number;
  };
}

export const ordersApi = {
  list: async (params: {
    storeId?: string;
    status?: OrderStatus;
    page?: number;
    limit?: number;
    phone?: string;
  } = {}): Promise<OrdersPage> => {
    try {
      const response = await apiClient.get("gestock/orders", {
        headers: authHeaders(),
        params: {
          storeId: params.storeId,
          status: params.status,
          page: params.page,
          limit: params.limit,
          phone: params.phone,
        },
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger les commandes.");
    }
  },

  updateStatus: async (
    orderId: string,
    patch: { status?: OrderStatus; note?: string; internalNotes?: string; followUpAt?: string },
    storeId?: string
  ): Promise<{ order: StoreOrder }> => {
    try {
      const url = storeId
        ? `gestock/stores/${storeId}/orders/${orderId}`
        : `gestock/orders/${orderId}`;
      const response = await apiClient.patch(url, patch, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Mise à jour impossible.");
    }
  },

  /** WhatsApp deep link to answer the buyer. */
  whatsappLink: (order: StoreOrder): string => {
    const phone = String(order.customerPhone || "").replace(/\D/g, "");
    const message = `Bonjour ${order.customerName || ""}, concernant votre commande ${order.reference}.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  },
};

export default ordersApi;
